/**
 * SortableRow - A draggable row in the email builder
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmailRow } from './types';
import { GripVertical, Trash2, Copy } from 'lucide-react';

interface SortableRowProps {
  row: EmailRow;
  isSelected: boolean;
  hasSelectedBlock: boolean; // True if any block inside this row is selected
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  children: React.ReactNode;
  showDropIndicatorAbove?: boolean;
  showDropIndicatorBelow?: boolean;
}

export const SortableRow: React.FC<SortableRowProps> = ({
  row,
  isSelected,
  hasSelectedBlock,
  onSelect,
  onDelete,
  onDuplicate,
  children,
  showDropIndicatorAbove = false,
  showDropIndicatorBelow = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: row.id,
    data: { type: 'row', rowId: row.id },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: row.backgroundColor || '#ffffff',
    padding: row.padding,
    minHeight: '40px',
    // Background image support
    ...(row.backgroundImage && {
      backgroundImage: `url('${row.backgroundImage}')`,
      backgroundSize: row.backgroundSize || 'cover',
      backgroundPosition: row.backgroundPosition || 'center',
      backgroundRepeat: row.backgroundRepeat || 'no-repeat',
    }),
  };

  // Only show row controls when row is explicitly selected (not a block inside)
  const showControls = isSelected && !hasSelectedBlock;

  return (
    <div className="relative">
      {/* Drop Indicator - Above */}
      {showDropIndicatorAbove && (
        <div className="relative z-50 pointer-events-none" style={{ height: '3px', marginBottom: '-1px' }}>
          <div className="absolute inset-x-0 h-0.5 bg-blue-500" style={{ top: '50%', transform: 'translateY(-50%)' }} />
        </div>
      )}
      
      <div
        ref={setNodeRef}
        style={style}
        className={`relative ${isDragging ? 'opacity-30 z-50' : ''} ${
          showControls ? 'ring-2 ring-blue-500 ring-inset' : ''
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
      {/* Row Controls - only visible when this row is hovered or selected */}
      <div
        className={`absolute -left-10 top-0 bottom-0 w-9 flex flex-col items-center justify-center gap-1 transition-opacity z-10 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab active:cursor-grabbing rounded shadow-sm"
          style={{ backgroundColor: '#ffffff', color: '#374151', border: '1px solid #d1d5db' }}
          title="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>

        {/* Duplicate */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-1 rounded shadow-sm hover:bg-blue-50"
          style={{ backgroundColor: '#ffffff', color: '#374151', border: '1px solid #d1d5db' }}
          title="Duplicate row"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>

        {/* Delete */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 rounded shadow-sm hover:bg-red-50"
          style={{ backgroundColor: '#ffffff', color: '#374151', border: '1px solid #d1d5db' }}
          title="Delete row"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row Content - no extra margin, render directly */}
      <div>{children}</div>

      {/* Row layout indicator - subtle, top right */}
      <div
        className={`absolute right-1 top-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#6b7280', border: '1px solid #e5e7eb' }}
      >
        {row.layout}
      </div>
      </div>
      
      {/* Drop Indicator - Below (rendered after the row) */}
      {showDropIndicatorBelow && (
        <div className="relative z-50 pointer-events-none" style={{ height: '3px', marginTop: '-1px' }}>
          <div className="absolute inset-x-0 h-0.5 bg-blue-500" style={{ top: '50%', transform: 'translateY(-50%)' }} />
        </div>
      )}
    </div>
  );
};

